import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import FontSizeControl from '../FontSizeControl/FontSizeControl';
import ProfileMenu from '../ProfileMenu/ProfileMenu';
import { useAuth } from '../../contexts/AuthContext';
import { sessionSaveService } from '../../services/sessionSaveService';
import { getSessionToken } from '../../services/sessionTokenService';

interface HeaderProps {
  showSignUp?: boolean;
  showLogIn?: boolean;
  sticky?: boolean;
}

const Header: React.FC<HeaderProps> = ({ 
  showSignUp = true, 
  showLogIn = true,
  sticky = true
}) => {
  const { isAuthenticated, user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthenticatedMobileMenuOpen, setIsAuthenticatedMobileMenuOpen] = useState(false);
  const [isTutorialsMenuOpen, setIsTutorialsMenuOpen] = useState(false);
  const tutorialsMenuRef = useRef<HTMLDivElement | null>(null);

  // Refetch user profile when navigating to daily page after authentication
  // This ensures the PRO tag appears immediately after login
  useEffect(() => {
    if (isAuthenticated && location.pathname === '/daily') {
      const token = localStorage.getItem('accessToken');
      if (token) {
        const sessionToken = getSessionToken();
        const headers: Record<string, string> = {
          'Authorization': `Bearer ${token}`,
        };
        if (sessionToken) {
          headers['X-Session-Token'] = sessionToken;
        }
        
        // Refetch profile to ensure subscription status is up to date
        // This is especially important right after login when navigating to /daily
        fetch('/api/v1/auth/profile', {
          headers,
        })
          .then(response => {
            if (response.ok) {
              return response.json();
            }
            return null;
          })
          .then(userData => {
            if (userData) {
              // Only update if subscription status differs to avoid unnecessary re-renders
              const cachedUser = localStorage.getItem('user');
              const cachedUserData = cachedUser ? JSON.parse(cachedUser) : null;
              
              if (!cachedUserData || cachedUserData.hasProSubscription !== userData.hasProSubscription) {
                localStorage.setItem('user', JSON.stringify(userData));
                // Update state by calling login with existing token and new user data
                login(token, userData);
              }
            }
          })
          .catch(error => {
            // Silently fail - don't show errors for profile refetch
            console.log('Header: Error refetching profile:', error);
          });
      }
    }
  }, [isAuthenticated, location.pathname, login]); // Re-run when authentication status or location changes

  // Close Tutorials dropdown when clicking outside (desktop and mobile)
  useEffect(() => {
    if (!isTutorialsMenuOpen) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (!tutorialsMenuRef.current) return;
      const target = event.target as Node;
      if (!tutorialsMenuRef.current.contains(target)) {
        setIsTutorialsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isTutorialsMenuOpen]);

  // Check if we're on the Landing page
  const isLandingPage = location.pathname === '/' || location.pathname === '/landing';

  // Handle logo click navigation
  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.scrollTo(0, 0);
    
    // If authenticated, always go to ListDaily page
    // If not authenticated, go to Landing page
    if (isAuthenticated) {
      navigate('/daily');
    } else {
      navigate('/');
    }
  };

  // Handle Sign Up button click with session data saving
  const handleSignUpClick = () => {
    console.log('🚨 DEBUG: handleSignUpClick function called!');
    console.log('🔍 Header Sign Up button clicked');
    console.log('🔍 Current location:', location.pathname);
    console.log('🔍 Is authenticated:', isAuthenticated);
    
    // Only save session data if we're on a List* page
    const isOnListPage = ['/daily', '/technicals', '/fundamentals', '/analysts', '/'].includes(location.pathname);
    console.log('🔍 Is on List page:', isOnListPage);
    
    if (isOnListPage && !isAuthenticated) {
      console.log('🔍 Header Sign Up button clicked on List page, saving session data');
      
      // Use the global session save service
      const sessionSaved = sessionSaveService.saveCurrentSessionToStorage();
      
      if (sessionSaved) {
        console.log('✅ Session data saved successfully');
      } else {
        console.log('⚠️ No session data to save');
      }
      
      // Navigate to signup with session data context
      // Include search params to detect if coming from a public list
      const fullPath = location.pathname === '/' ? '/daily' : location.pathname;
      const fullPathWithSearch = location.search ? `${fullPath}${location.search}` : fullPath;
      navigate('/signup', {
        state: {
          fromSignUp: true,
          returnPath: fullPathWithSearch,
          isFromPublicList: location.search?.includes('public_uuid')
        }
      });
      return;
    }
    
    console.log('🔍 Default navigation without session data');
    // Default navigation without session data
    navigate('/signup');
  };
  return (
    <header className={`${sticky ? 'sticky top-0' : ''} z-[100] bg-surface-light dark:bg-background-card border-b border-border-light dark:border-border-color h-16 px-6`}>
      <div className="flex justify-between items-center h-full">
        {/* Logo, title, and upgrade button */}
        <div className="flex items-center gap-4">
          <div 
            onClick={handleLogoClick}
            className="flex items-center gap-3 hover:opacity-80 dark:hover:opacity-100 transition-opacity duration-200 cursor-pointer"
          >
            <div className="h-8 w-8 rounded-lg overflow-hidden flex-shrink-0">
              <svg className="h-full w-full" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                <rect width="100" height="100" fill="#FFD700" rx="8"/>
                <text x="14" y="72" fontFamily="Arial, sans-serif" fontSize="62" fontWeight="900" fill="#000000" stroke="#000000" strokeWidth="1">S</text>
                <text x="54" y="72" fontFamily="Arial, sans-serif" fontSize="46" fontWeight="900" fill="#000000" stroke="#000000" strokeWidth="2">T</text>
              </svg>
            </div>
            <h1 className="text-text-light-primary dark:text-text-dark-primary text-xl font-bold tracking-wide flex items-center gap-2">
              Stock Table
              {user?.hasProSubscription && (
                <span className="px-1.5 py-0.5 text-xs font-semibold text-gray-900 bg-primary/30 dark:bg-highlight-gold/20 dark:text-highlight-gold rounded">
                  PRO
                </span>
              )}
            </h1>
          </div>
          
          {/* Upgrade button - show when authenticated without Pro subscription */}
          {isAuthenticated && !user?.hasProSubscription && (
            <button
              onClick={() => navigate('/pricing', { state: { fromUpgrade: true } })}
              className="bg-primary dark:bg-highlight-gold border-2 border-primary dark:border-highlight-gold text-gray-900 px-3 py-1 rounded-full font-semibold hover:opacity-90 transition-colors duration-200 text-sm"
            >
              Upgrade
            </button>
          )}
        </div>

        {/* Desktop menu - only show on large screens */}
        <div className="hidden lg:flex items-center gap-4">
          {/* Navigation links */}
          <div className="flex items-center gap-6 mr-4">
            <div className="relative" ref={tutorialsMenuRef}>
              <button
                type="button"
                onClick={() => setIsTutorialsMenuOpen((open) => !open)}
                className="inline-flex items-center gap-1 text-text-light-secondary dark:text-white hover:text-gold-text-light dark:hover:text-highlight-gold font-semibold transition-colors duration-200 text-sm"
              >
                Tutorials
                <svg
                  className={`w-4 h-4 transition-transform duration-150 ${isTutorialsMenuOpen ? 'rotate-180' : ''}`}
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.5 7.5L10 12L14.5 7.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {isTutorialsMenuOpen && (
                <div className="absolute left-0 mt-2 w-56 rounded-md bg-surface-light dark:bg-background-card shadow-lg border border-border-light dark:border-border-color z-[150]">
                  <div className="py-2">
                    <Link
                      to="/tutorials/technicals"
                      onClick={() => setIsTutorialsMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-text-light-secondary dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gold-text-light dark:hover:text-highlight-gold"
                    >
                      Technical Indicators
                    </Link>
                    <Link
                      to="/tutorials/fundamentals"
                      onClick={() => setIsTutorialsMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-text-light-secondary dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gold-text-light dark:hover:text-highlight-gold"
                    >
                      Fundamental Indicators
                    </Link>
                  </div>
                </div>
              )}
            </div>
            <Link to="/pricing" className="text-text-light-secondary dark:text-white hover:text-gold-text-light dark:hover:text-highlight-gold font-semibold transition-colors duration-200 text-sm">
              Pricing
            </Link>
            <Link to="/about-us" className="text-text-light-secondary dark:text-white hover:text-gold-text-light dark:hover:text-highlight-gold font-semibold transition-colors duration-200 text-sm">
              About Us
            </Link>
          </div>
          
          {/* Theme toggle */}
          <ThemeToggle />
          
          {/* Font size control */}
          <FontSizeControl />
          
          {isAuthenticated ? (
            <ProfileMenu />
          ) : (
            <>
              {showSignUp && (
                <>
                  {isLandingPage ? (
                    <Link 
                      to="/get-started"
                      className="bg-primary text-gray-900 px-4 py-2 rounded-md font-semibold hover:bg-opacity-90 transition-colors duration-200 text-sm inline-block"
                    >
                      Get Started
                    </Link>
                  ) : (
                    <button 
                      onClick={() => {
                        console.log('🚨 BUTTON CLICKED!');
                        handleSignUpClick();
                      }}
                      className="bg-primary text-gray-900 px-4 py-2 rounded-md font-semibold hover:bg-opacity-90 transition-colors duration-200 text-sm"
                    >
                      Sign Up
                    </button>
                  )}
                </>
              )}
              {!showSignUp && showLogIn && (
                <div className="bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-4 py-2 rounded-md font-semibold text-sm cursor-not-allowed">
                  {isLandingPage ? 'Get Started' : 'Sign Up'}
                </div>
              )}
              {showLogIn && (
                <Link to="/login" className="text-text-light-secondary dark:text-white hover:text-gold-text-light dark:hover:text-highlight-gold font-semibold transition-colors duration-200 text-sm">
                  Log In
                </Link>
              )}
            </>
          )}
        </div>

        {/* Mobile hamburger menu - only show for unauthenticated users (small and medium screens) */}
        {!isAuthenticated && (
          <div className="lg:hidden flex items-center gap-5">
            {/* Hamburger button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-md text-text-light-secondary dark:text-text-dark-secondary hover:text-text-light-primary dark:hover:text-text-dark-primary hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        )}

        {/* Mobile menu for authenticated users - show hamburger menu and profile menu (small and medium screens) */}
        {isAuthenticated && (
          <div className="lg:hidden flex items-center gap-5">
            {/* Hamburger button for authenticated users */}
            <button
              onClick={() => setIsAuthenticatedMobileMenuOpen(!isAuthenticatedMobileMenuOpen)}
              className="p-2 rounded-md text-text-light-secondary dark:text-text-dark-secondary hover:text-text-light-primary dark:hover:text-text-dark-primary hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isAuthenticatedMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
            <ProfileMenu />
          </div>
        )}
      </div>

      {/* Mobile menu dropdown for authenticated users */}
      {isAuthenticated && isAuthenticatedMobileMenuOpen && (
        <>
          {/* Backdrop overlay - only covers content below header */}
          <div 
            className="fixed top-16 left-0 right-0 bottom-0 bg-black/50 z-[99] lg:hidden"
            onClick={() => setIsAuthenticatedMobileMenuOpen(false)}
          />
          
          {/* Menu dropdown */}
          <div className="lg:hidden mt-4 py-4 border-t border-border-light dark:border-border-color bg-surface-light dark:bg-background-card -mx-6 px-6 relative z-[101]">
            <div className="flex flex-col gap-3">
              {/* Upgrade button - show when authenticated without Pro subscription */}
              {!user?.hasProSubscription && (
                <button
                  onClick={() => {
                    navigate('/pricing', { state: { fromUpgrade: true } });
                    setIsAuthenticatedMobileMenuOpen(false);
                  }}
                  className="w-full bg-primary dark:bg-highlight-gold border-2 border-primary dark:border-highlight-gold text-gray-900 px-3 py-1 rounded-full font-semibold hover:opacity-90 transition-colors duration-200 text-sm"
                >
                  Upgrade
                </button>
              )}
              
              {/* Navigation links */}
              <Link 
                to="/tutorials/technicals" 
                onClick={() => setIsAuthenticatedMobileMenuOpen(false)}
                className="w-full text-center text-text-light-secondary dark:text-white hover:text-gold-text-light dark:hover:text-highlight-gold font-semibold transition-colors duration-200 text-sm py-2"
              >
                Technical Tutorials
              </Link>
              <Link 
                to="/tutorials/fundamentals" 
                onClick={() => setIsAuthenticatedMobileMenuOpen(false)}
                className="w-full text-center text-text-light-secondary dark:text-white hover:text-gold-text-light dark:hover:text-highlight-gold font-semibold transition-colors duration-200 text-sm py-2"
              >
                Fundamental Tutorials
              </Link>
              <Link 
                to="/pricing" 
                onClick={() => setIsAuthenticatedMobileMenuOpen(false)}
                className="w-full text-center text-text-light-secondary dark:text-white hover:text-gold-text-light dark:hover:text-highlight-gold font-semibold transition-colors duration-200 text-sm py-2"
              >
                Pricing
              </Link>
              <Link 
                to="/about-us" 
                onClick={() => setIsAuthenticatedMobileMenuOpen(false)}
                className="w-full text-center text-text-light-secondary dark:text-white hover:text-gold-text-light dark:hover:text-highlight-gold font-semibold transition-colors duration-200 text-sm py-2"
              >
                About Us
              </Link>
              
              {/* Divider */}
              <div className="border-t border-border-light dark:border-border-color my-1" />
              
              {/* Theme toggle */}
              <div className="flex items-center justify-between px-4 py-2">
                <span className="text-sm font-medium text-text-light-secondary dark:text-white">Theme</span>
                <ThemeToggle />
              </div>
              
              {/* Font size control */}
              <div className="flex items-center justify-between px-4 py-2">
                <span className="text-sm font-medium text-text-light-secondary dark:text-white">Font Size</span>
                <FontSizeControl />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Mobile menu dropdown - only show for unauthenticated users */}
      {!isAuthenticated && isMobileMenuOpen && (
        <>
          {/* Backdrop overlay - only covers content below header */}
          <div 
            className="fixed top-16 left-0 right-0 bottom-0 bg-black/50 z-[99] lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          {/* Menu dropdown */}
          <div className="lg:hidden mt-4 py-4 border-t border-border-light dark:border-border-color bg-surface-light dark:bg-background-card -mx-6 px-6 relative z-[101]">
            <div className="flex flex-col gap-3">
              {/* Navigation links */}
              <Link 
                to="/tutorials/technicals" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-full text-center text-text-light-secondary dark:text-white hover:text-gold-text-light dark:hover:text-highlight-gold font-semibold transition-colors duration-200 text-sm py-2"
              >
                Technical Tutorials
              </Link>
              <Link 
                to="/tutorials/fundamentals" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-full text-center text-text-light-secondary dark:text-white hover:text-gold-text-light dark:hover:text-highlight-gold font-semibold transition-colors duration-200 text-sm py-2"
              >
                Fundamental Tutorials
              </Link>
              <Link 
                to="/pricing" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-full text-center text-text-light-secondary dark:text-white hover:text-gold-text-light dark:hover:text-highlight-gold font-semibold transition-colors duration-200 text-sm py-2"
              >
                Pricing
              </Link>
              <Link 
                to="/about-us" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-full text-center text-text-light-secondary dark:text-white hover:text-gold-text-light dark:hover:text-highlight-gold font-semibold transition-colors duration-200 text-sm py-2"
              >
                About Us
              </Link>
              
              {/* Divider */}
              <div className="border-t border-border-light dark:border-border-color my-1" />
              
              {/* Theme toggle */}
              <div className="flex items-center justify-between px-4 py-2">
                <span className="text-sm font-medium text-text-light-secondary dark:text-white">Theme</span>
                <ThemeToggle />
              </div>
              
              {/* Font size control */}
              <div className="flex items-center justify-between px-4 py-2">
                <span className="text-sm font-medium text-text-light-secondary dark:text-white">Font Size</span>
                <FontSizeControl />
              </div>
              
              {/* Divider */}
              <div className="border-t border-border-light dark:border-border-color my-1" />
              
              {showSignUp && (
                <>
                  {isLandingPage ? (
                    <Link 
                      to="/get-started"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="w-full text-center bg-primary text-gray-900 px-4 py-2 rounded-md font-semibold hover:bg-opacity-90 transition-colors duration-200 text-sm inline-block"
                    >
                      Get Started
                    </Link>
                  ) : (
                    <button 
                      onClick={() => {
                        console.log('🚨 MOBILE BUTTON CLICKED!');
                        handleSignUpClick();
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full bg-primary text-gray-900 px-4 py-2 rounded-md font-semibold hover:bg-opacity-90 transition-colors duration-200 text-sm"
                    >
                      Sign Up
                    </button>
                  )}
                </>
              )}
              {!showSignUp && showLogIn && (
                <div className="w-full bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-4 py-2 rounded-md font-semibold text-sm cursor-not-allowed">
                  {isLandingPage ? 'Get Started' : 'Sign Up'}
                </div>
              )}
              {showLogIn && (
                <Link 
                  to="/login" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="w-full text-center text-text-light-secondary dark:text-white hover:text-gold-text-light dark:hover:text-highlight-gold font-semibold transition-colors duration-200 text-sm py-2"
                >
                  Log In
                </Link>
              )}
            </div>
          </div>
        </>
      )}
    </header>
  );
};

export default Header;
